import { useState } from 'react';
import { useMarkPaid } from '@/api/studio';
import type { Invoice, ManualPaymentMethod } from '@/api/types';
import { Dialog } from '@/components/ui';
import { balanceOf, formatMoney } from '@/lib/format';
import { manualMethodLabel } from '@/lib/status';
import { toast } from '@/lib/toast';
import { parseMajor } from './invoiceList';

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** The studio records a payment by hand: date, amount (the balance by default), method, a comment. */
export function MarkPaidDialog({ invoice, onClose }: { invoice?: Invoice; onClose: () => void }) {
  return (
    <Dialog open={!!invoice} onClose={onClose} title="Отметить оплату">
      {invoice && <MarkPaidForm key={invoice.id} invoice={invoice} onClose={onClose} />}
    </Dialog>
  );
}

function MarkPaidForm({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const mark = useMarkPaid();
  const balance = balanceOf(invoice);
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState(String(balance.amountMinor / 100).replace('.', ','));
  const [method, setMethod] = useState<ManualPaymentMethod>('transfer');
  const [note, setNote] = useState('');
  const minor = parseMajor(amount);
  const amountBad = !(minor > 0 && minor <= balance.amountMinor);
  const dateBad = !date || date > today();

  return (
    <form
      className="space-y-4 p-5 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (amountBad || dateBad) return;
        mark.mutate(
          { id: invoice.id, input: { date, amountMinor: minor, method, note: note.trim() || undefined } },
          {
            onSuccess: () => {
              toast('Оплата записана');
              onClose();
            },
          },
        );
      }}
    >
      <p>
        Счёт № {invoice.number}
        {invoice.clientName ? ` для «${invoice.clientName}»` : ''}, к оплате {formatMoney(balance)}. Оплата попадёт в выписку и в Invoice Ninja; отменить её можно из выписки.
      </p>
      <label className="block">
        <span className="mb-1.5 block text-muted">Дата оплаты</span>
        <input className="field" type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} />
      </label>
      <label className="block">
        <span className="mb-1.5 block text-muted">Сумма</span>
        <input className="field" inputMode="decimal" value={amount} aria-invalid={amountBad} onChange={(e) => setAmount(e.target.value)} />
        {amountBad && <span className="mt-1 block text-xs text-overdue">От 0 до остатка: {formatMoney(balance)}</span>}
      </label>
      <label className="block">
        <span className="mb-1.5 block text-muted">Способ</span>
        <select className="field" value={method} onChange={(e) => setMethod(e.target.value as ManualPaymentMethod)}>
          {(Object.keys(manualMethodLabel) as ManualPaymentMethod[]).map((k) => (
            <option key={k} value={k}>
              {manualMethodLabel[k]}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="mb-1.5 block text-muted">Комментарий</span>
        <input className="field" value={note} maxLength={200} placeholder="Например, номер платёжки" onChange={(e) => setNote(e.target.value)} />
      </label>
      {mark.error && (
        <p role="alert" className="text-overdue">
          {mark.error.message}
        </p>
      )}
      <div className="flex gap-2">
        <button type="button" className="btn-secondary flex-1" onClick={onClose}>
          Отмена
        </button>
        <button type="submit" className="btn-primary flex-1" disabled={mark.isPending || amountBad || dateBad}>
          Записать оплату
        </button>
      </div>
    </form>
  );
}
